// ============================================================================
// email-recipient-memory.ts: this device's memory of who a quote was emailed
// to, by strata plan and by requester firm.
// ----------------------------------------------------------------------------
// PER BUSINESS (2026-10-13, branch claude/crm-requesters-email). Every read
// and write names the signed-in business: the store key ends in
// `::<tenantId>`, and without a tenant nothing is read or written. Before this
// the two stores were shared by every business signed in on the device, so
// one business's recipients could prefill another's email.
//
// The two old, unscoped stores (the *_v1 keys) are never read directly any
// more. adoptLegacyRecipientMemory moves a record into a business's store
// only when that record carries one of the business's OWN addresses (its
// admin CC, contact email or mail domains), which only that business would
// have typed; every other record stays where it was, unread.
// ============================================================================
interface StorageLike {
    getItem?: (key: string) => string | null;
    setItem?: (key: string, value: string) => void;
    removeItem?: (key: string) => void;
}

type RecipientMemory = Record<string, any>;

/** The old unscoped store keys. Read only by adoptLegacyRecipientMemory. */
export const EMAIL_RECIPIENT_MEMORY_STORAGE_KEY = 'qm_email_recipients_by_sp_v1';
export const EMAIL_REQUESTER_RECIPIENT_MEMORY_STORAGE_KEY = 'qm_email_recipients_by_requester_v1';

/** The per-business store key, or '' without a business. */
export function tenantRecipientMemoryKey(baseKey: string, tenantId: unknown): string {
    const tenant = String(tenantId ?? '').trim();
    return tenant ? `${baseKey}::${encodeURIComponent(tenant)}` : '';
}

const safeStorageGet = (storage: StorageLike | undefined | null, key: string): string | null => {
    try { return storage?.getItem?.(key) || null; } catch { return null; }
};

const safeStorageSet = (storage: StorageLike | undefined | null, key: string, value: string): void => {
    try { storage?.setItem?.(key, value); } catch {}
};

const safeStorageRemove = (storage: StorageLike | undefined | null, key: string): void => {
    try { storage?.removeItem?.(key); } catch {}
};

export function normalizeStrataPlanEmailKey(value: unknown): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const compact = raw.replace(/[\s._-]+/g, '').toUpperCase();
    const digitMatch = compact.match(/(?:SP|STRATAPLAN)?(\d{2,8})/i);
    if (digitMatch) return `SP${digitMatch[1]}`;
    return compact.replace(/[^A-Z0-9]/g, '').slice(0, 32);
}

export function normalizeRequesterCompanyEmailKey(value: unknown): string {
    const raw = String(value || '').trim().replace(/\s+/g, ' ');
    if (!raw) return '';
    return raw
        .toUpperCase()
        .replace(/&/g, 'AND')
        .replace(/[^A-Z0-9]+/g, '')
        .slice(0, 48);
}

export function normalizeEmailList(value: unknown): string {
    const seen = new Set<string>();
    return String(value || '')
        .replace(/[\r\n]+/g, ', ')
        .split(/[;,]+/)
        .map(part => part.trim().replace(/\s+/g, ' '))
        .filter(Boolean)
        .filter(part => {
            const key = part.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .join(', ');
}

/** The addresses of a normalised list, lower case. */
export function emailListAddresses(value: unknown): string[] {
    return normalizeEmailList(value).split(', ').map(part => part.trim().toLowerCase()).filter(Boolean);
}

function loadRecipientMemory(storageKey: string, storage: StorageLike | undefined | null = globalThis.localStorage): RecipientMemory {
    if (!storageKey) return {};
    try {
        const parsed = JSON.parse(safeStorageGet(storage, storageKey) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

export function loadEmailRecipientMemory(tenantId: unknown, storage: StorageLike | undefined | null = globalThis.localStorage): RecipientMemory {
    return loadRecipientMemory(tenantRecipientMemoryKey(EMAIL_RECIPIENT_MEMORY_STORAGE_KEY, tenantId), storage);
}

export function loadRequesterRecipientMemory(tenantId: unknown, storage: StorageLike | undefined | null = globalThis.localStorage): RecipientMemory {
    return loadRecipientMemory(tenantRecipientMemoryKey(EMAIL_REQUESTER_RECIPIENT_MEMORY_STORAGE_KEY, tenantId), storage);
}

export function getEmailRecipientsForStrataPlan(strataPlan: unknown, tenantId: unknown, storage: StorageLike | undefined | null = globalThis.localStorage) {
    const key = normalizeStrataPlanEmailKey(strataPlan);
    if (!key || !tenantRecipientMemoryKey(EMAIL_RECIPIENT_MEMORY_STORAGE_KEY, tenantId)) return null;
    const record = loadEmailRecipientMemory(tenantId, storage)[key];
    if (!record || typeof record !== 'object') return null;
    return {
        key,
        to: normalizeEmailList(record.to),
        cc: normalizeEmailList(record.cc),
        updatedAt: record.updatedAt || null,
    };
}

export function getEmailRecipientsForRequesterCompany(companyName: unknown, tenantId: unknown, storage: StorageLike | undefined | null = globalThis.localStorage) {
    const key = normalizeRequesterCompanyEmailKey(companyName);
    if (!key || !tenantRecipientMemoryKey(EMAIL_REQUESTER_RECIPIENT_MEMORY_STORAGE_KEY, tenantId)) return null;
    const record = loadRequesterRecipientMemory(tenantId, storage)[key];
    if (!record || typeof record !== 'object') return null;
    return {
        key,
        label: record.label || String(companyName || '').trim(),
        to: normalizeEmailList(record.to),
        cc: normalizeEmailList(record.cc),
        updatedAt: record.updatedAt || null,
    };
}

export function rememberEmailRecipientsForStrataPlan(strataPlan: unknown, recipients: { to?: unknown; cc?: unknown } = {}, tenantId: unknown = null, storage: StorageLike | undefined | null = globalThis.localStorage) {
    const key = normalizeStrataPlanEmailKey(strataPlan);
    const storageKey = tenantRecipientMemoryKey(EMAIL_RECIPIENT_MEMORY_STORAGE_KEY, tenantId);
    if (!key || !storageKey) return null;
    const to = normalizeEmailList(recipients.to);
    const cc = normalizeEmailList(recipients.cc);
    if (!to && !cc) return null;
    const memory = loadEmailRecipientMemory(tenantId, storage);
    const record = {
        ...(memory[key] && typeof memory[key] === 'object' ? memory[key] : {}),
        to,
        cc,
        updatedAt: new Date().toISOString(),
    };
    memory[key] = record;
    safeStorageSet(storage, storageKey, JSON.stringify(memory));
    return { key, ...record };
}

export function rememberEmailRecipientsForRequesterCompany(companyName: unknown, recipients: { to?: unknown; cc?: unknown } = {}, tenantId: unknown = null, storage: StorageLike | undefined | null = globalThis.localStorage) {
    const label = String(companyName || '').trim().replace(/\s+/g, ' ');
    const key = normalizeRequesterCompanyEmailKey(label);
    const storageKey = tenantRecipientMemoryKey(EMAIL_REQUESTER_RECIPIENT_MEMORY_STORAGE_KEY, tenantId);
    if (!key || !storageKey) return null;
    const to = normalizeEmailList(recipients.to);
    const cc = normalizeEmailList(recipients.cc);
    if (!to && !cc) return null;
    const memory = loadRequesterRecipientMemory(tenantId, storage);
    const record = {
        ...(memory[key] && typeof memory[key] === 'object' ? memory[key] : {}),
        label,
        to,
        cc,
        updatedAt: new Date().toISOString(),
    };
    memory[key] = record;
    safeStorageSet(storage, storageKey, JSON.stringify(memory));
    return { key, ...record };
}

/**
 * Move the old unscoped records that belong to this business into its own
 * stores. A record belongs to it only when one of its To or Cc addresses is
 * one of `own.emails` or on one of `own.domains` (the business's admin CC,
 * contact email and mail domains). A public mail domain is never passed as
 * own. A record the business already has a newer copy of is not replaced.
 * Moved records leave the old store; the rest stay there, unread. Returns
 * how many records moved.
 */
export function adoptLegacyRecipientMemory(
    tenantId: unknown,
    own: { emails?: unknown[]; domains?: unknown[] },
    storage: StorageLike | undefined | null = globalThis.localStorage,
): number {
    const emails = new Set((own.emails || []).map(value => String(value || '').trim().toLowerCase()).filter(value => value.includes('@')));
    const domains = new Set((own.domains || []).map(value => String(value || '').trim().toLowerCase().replace(/^@/, '')).filter(Boolean));
    if (!tenantRecipientMemoryKey(EMAIL_RECIPIENT_MEMORY_STORAGE_KEY, tenantId) || (!emails.size && !domains.size)) return 0;
    const belongs = (record: any) => [...emailListAddresses(record?.to), ...emailListAddresses(record?.cc)]
        .some(address => emails.has(address) || domains.has(address.split('@')[1] || ''));
    let moved = 0;
    for (const base of [EMAIL_RECIPIENT_MEMORY_STORAGE_KEY, EMAIL_REQUESTER_RECIPIENT_MEMORY_STORAGE_KEY]) {
        const legacy = loadRecipientMemory(base, storage);
        const keys = Object.keys(legacy).filter(key => legacy[key] && typeof legacy[key] === 'object' && belongs(legacy[key]));
        if (!keys.length) continue;
        const scopedKey = tenantRecipientMemoryKey(base, tenantId);
        const scoped = loadRecipientMemory(scopedKey, storage);
        for (const key of keys) {
            const older = String(legacy[key].updatedAt || '') <= String(scoped[key]?.updatedAt || '');
            if (!scoped[key] || !older) scoped[key] = legacy[key];
            delete legacy[key];
            moved += 1;
        }
        safeStorageSet(storage, scopedKey, JSON.stringify(scoped));
        if (Object.keys(legacy).length) safeStorageSet(storage, base, JSON.stringify(legacy));
        else safeStorageRemove(storage, base);
    }
    return moved;
}
